import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Grid,
  Spin,
  theme,
} from "antd";
import { useTranslation } from "react-i18next";
import { useProfilePageSrv } from "./useProfilePageSrv";
import BrandColors from "@brand-colors";
import AddressVerification from "pages/AddressVerification/AddressVerification";
import useToasterMessage from "helpers/useToasterMessage";
import { handleErrorNavigation } from "pages/ErrorPage/ErrorHandler";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import whitelabelConfig from "@whitelabel-config";
import { User, Mail, Phone, MapPin, IdCard } from "lucide-react";
import { usePageTitle } from "helpers/usePageTitle";

const ProfilePage = () => {
  const { httpgetMember, httpgetDocument } = useProfilePageSrv();
  const [isHover, setIsHover] = useState(false);
  const [memberData, setMemberData] = useState();
  const [document, setDocument] = useState([]);
  const [addressEnabled, setAddressEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const { useToken } = theme;
  const { UseToaster } = useToasterMessage();
  const { t } = useTranslation();
  usePageTitle("PROFILE.PAGE_TITLE", `Profile - ${process.env.REACT_APP_PRODUCT}`);
  const { useBreakpoint } = Grid;
  const { token } = useToken();
  const screens = useBreakpoint();
  const navigate = useNavigate();

  const uiState = useSelector((state: any) => state.portal.uiState);
  const preferredLanguage = useSelector(
    (state: any) => state.portal.uiState.Language
  );
  console.log(preferredLanguage,"preferredLanguage")
  const [isRightAlign, setIsRightAlign] = useState(false);

  useEffect(() => {         try {
      if (whitelabelConfig.uiSettings.rightAlignedLanguage.includes(preferredLanguage) === true) {
        setIsRightAlign(true);
      } else {
        setIsRightAlign(false);
      }
    } catch (e) {
      console.log("Error", e)
      handleErrorNavigation(e, navigate, t);
    }
  }, [whitelabelConfig, preferredLanguage]);



  const getMember = async () => {
    try {
      const res = await httpgetMember();
      setMemberData(res?.data);
    } catch (e) {
      handleErrorNavigation(e, navigate, t);
    }
  };

  const getDocument = async () => {
    try {
      const res = await httpgetDocument();
      setDocument(res?.data);
    } catch (e) {
      handleErrorNavigation(e, navigate, t);
    }
  };

  useEffect(() => {
    Promise.all([getMember(), getDocument()]).finally(() => setLoading(false));
  }, []);

  const handleMouseEnter = () => {
    setIsHover(true);
  };
  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const validateAddress = () => {
    setAddressEnabled(true);
  };

  const handleChildData = (dataFromChild: any) => {
    console.log("Data from child:", dataFromChild);
    UseToaster({
      type: "success",
      message: dataFromChild,
      description: "",
    });
    setAddressEnabled(false);
    getMember();
    console.log("Data from child:", dataFromChild);
  };

  const styles = {
    container: {
      margin: "0 auto",
      maxWidth: "450px",
      textAlign: "center",
      backgroundColor: token.colorBgContainer,
      zIndex: 100,
    },
    currentdate: {
      fontWeight: 600,
      color: "#5e4ad2",
      fontSize: "18px",
    },

    header: {
      maxWidth: "85%",
      padding: "20px 0",
      margin: " 0 auto",
      fontWeight: "600",
    },
    cardbody: {
      fontSize: "16px",
      fontWeight: 600,
      color: "#333333",
    },
    mainheader: {
      height: "80px",
      padding: "15px 0",
      backgroundColor: BrandColors.HEADER_BG,
    },
    subheader: {
      maxWidth: "100%",
      borderTop: "1px solid #cccccc",
    },

    text: {
      color: "#333",
      fontSize: "12px",
      lineHeight: "21px",
    },

    button: {
      backgroundColor: isHover ? token.colorBgContainer : BrandColors.BUTTON_BG,
      borderColor: isHover ? BrandColors.BUTTON_BG : token.colorBgContainer,
      color: isHover ? BrandColors.BUTTON_BG : token.colorBgContainer,
      height: "42px",
      borderRadius: "50px",
      textTransform: "uppercase",
      fontWeight: "500",
      boxShadow: "none",
      fontSize: screens.md ? "14px" : "12px",
    },
    paragraph: {
      fontSize: screens.md ? "16px" : "14px",
      marginTop: "0",
      marginBottom: "12px",
      textAlign:  isRightAlign ? "right" : "left",
      display: "flex",
      flexDirection: isRightAlign ? "row-reverse" : "",
    },
  };
  console.log("memberData", memberData);
  console.log("document", document);
  const formatDate = (dateString: any) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const initials = `${(memberData?.FirstName?.[0] ?? "").toUpperCase()}${(memberData?.LastName?.[0] ?? "").toUpperCase()}`;
  const fullName = `${memberData?.FirstName ?? ""} ${memberData?.LastName ?? ""}`.trim();

  const SectionHeading = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "rgba(99, 102, 241, 0.10)",
          color: "#6366f1",
        }}
        id={`img-${text.toLowerCase().replace(/\s+/g, '-')}`} tabIndex={0} role="img" aria-label={`img-${text.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {icon}
      </span>
      <h3 id={`${text.toLowerCase().replace(/\s+/g, '-')}`} role="article" aria-label={`label-${text.toLowerCase().replace(/\s+/g, '-')}`}  tabIndex={0} style={{ margin: 0, fontSize: screens.md ? "18px" : "16px", fontWeight: 700, color: "#111827" }}>{text}</h3>
    </div>
  );

  const InfoRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 10,
        background: "#f9fafb",
        border: "1px solid #f1f5f9",
        marginBottom: 10,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 34,
          height: 34,
          borderRadius: 8,
          background: "#eef2ff",
          color: "#6366f1",
          flex: "0 0 auto",
        }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div id={`label-${label.toLowerCase().replace(/\s+/g, '-')}`} tabIndex={0} role="article" aria-label={`label-${label.toLowerCase().replace(/\s+/g, '-')}`}  style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
        <div id={`value-${value?.toLowerCase().replace(/\s+/g, '-')}`} tabIndex={0} role="article" aria-label={`value-${value?.toLowerCase().replace(/\s+/g, '-')}`}  style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginTop: 2, wordBreak: "break-word" }}>{value || "—"}</div>
      </div>
    </div>
  );

  return (
    <Spin spinning={loading}>
    <div
      className="row page-wrapper justify-content-md-center"
      data-testid="pass-page"
    >
      <div className="col-md-12 col-sm-12" style={{display: "flex", justifyContent: "center"}}>
        <Card className="home-widget-card hls-container" style={{ borderRadius: 12, textAlign: "left", border: "1px solid #eef0f4", boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)", width: "fit-content", maxWidth: "100%" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: screens.md ? "minmax(260px, 380px) minmax(420px, 460px)" : "1fr",
              gap: screens.md ? "32px" : "20px",
              alignItems: "start",
              justifyContent: "start",
              padding: screens.md ? "8px 4px" : "4px 0",
            }}
          >
            <div style={{ textAlign: isRightAlign ? "right" : "left" }}>
              <SectionHeading icon={<User size={18} />} text={t("PROFILE_PAGE.MEMEBERINFO_TEXT")} />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 16px",
                  borderRadius: 12,
                  background: "linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)",
                  border: "1px solid #e0e7ff",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: "#6366f1",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 18,
                    flex: "0 0 auto",
                    boxShadow: "0 4px 10px rgba(99, 102, 241, 0.25)",
                  }}
                  id="membersec-icon" tabIndex={0} role="article" aria-label="membersec-icon"
                >
                  {initials || <User size={22}  />}
                </div>
                <h2 style={{ minWidth: 0 }} id="name" tabIndex={0} role="heading" aria-level={2} >
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>{fullName || "—"}</div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#6b7280", marginTop: 4, display: "inline-flex", alignItems: "center", gap: 6 }}></div>
                </h2>
              </div>

              <InfoRow icon={<User size={16} id="firstname-icon" tabIndex={0} aria-hidden="false" role="img" aria-label="firstname-icon" />} label={t("PROFILE_PAGE.FIRST_NAME")} value={memberData?.FirstName} />
              <InfoRow icon={<User size={16} id="lastname-icon" tabIndex={0} aria-hidden="false" role="img" aria-label="lastname-icon" />} label={t("PROFILE_PAGE.LAST_NAME")} value={memberData?.LastName} />
              <InfoRow icon={<Mail size={16} id="email-icon" tabIndex={0} aria-hidden="false" role="img" aria-label="email-icon" />} label={t("PROFILE_PAGE.EMAIL")} value={memberData?.Email} />
              <InfoRow icon={<Phone size={16} id="mobile-number-icon" tabIndex={0} aria-hidden="false" role="img" aria-label="mobile-number-icon" />} label={t("PROFILE_PAGE.MOBILE_NUMBER")} value={memberData?.MobileNumber} />
            </div>

            <div
              style={{
                textAlign: isRightAlign ? "right" : "left",
                borderLeft: screens.md ? "1px solid #e5e7eb" : "none",
                borderTop: screens.md ? "none" : "1px solid #e5e7eb",
                paddingLeft: screens.md ? "32px" : "0",
                paddingTop: screens.md ? "0" : "20px",
              }}
            >
              <SectionHeading icon={<MapPin size={18} />} text={t("PROFILE_PAGE.ADDRESS_INFORMATION")} />
              <div className="card-body thank-container" style={{ marginLeft: "-25px", marginTop: "-15px" }}>
                <AddressVerification onDataFromChild={handleChildData} memberData={memberData} addressEnabled={addressEnabled} />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
    </Spin>
  );
};

export default ProfilePage;
